import { z } from "zod";

import { npiEnumerationTypeSchema } from "@/src/lib/npi-enumeration";
import type { AddressParts } from "@/src/models/address";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import type { Patient } from "@/src/models/patient";
import type { OrderPriorAuthRequirement } from "@/src/models/patient-order-requirement";
import { type Practitioner } from "@/src/models/practitioner";

export enum OrderType {
  CONSULT = "consult",
  LAB = "lab",
  IMAGING = "imaging",
  PRESCRIPTION = "prescription",
  PROCEDURE = "procedure",
  OTHER = "other",
}

export enum OrderVerificationDecision {
  PROCESS = "process",
  ESCALATE = "escalate",
}

export enum OrderEscalationReason {
  ADDITIONAL_CLINICAL_INFORMATION = "additional_clinical_information",
  ATHENA_SUBMISSION_FAILED = "athena_submission_failed",
  AUTH_DENIED = "auth_denied",
  AUTH_SUBMISSION_UNSUCCESSFUL = "auth_submission_unsuccessful",
  AUTHORIZATION_DOCUMENTATION_ISSUE = "authorization_documentation_issue",
  AUTHORIZATION_PENDING = "authorization_pending",
  AUTHORIZATION_REQUIREMENT_UNCLEAR = "authorization_requirement_unclear",
  INSURANCE_INACTIVE_OR_INVALID = "insurance_inactive_or_invalid",
  INTERNAL_ORDER_OUT_OF_VH_SCOPE = "internal_order_out_of_vh_scope",
  MANUAL_PROCESSING = "manual_processing",
  NO_DIGITAL_AUTH_PROMPT_TO_CALL = "no_digital_auth_prompt_to_call",
  NO_DIGITAL_AUTH_UPLOADED_FAX_FORM = "no_digital_auth_uploaded_fax_form",
  NO_IN_NETWORK_PROVIDER = "no_in_network_provider",
  OPEN_ENCOUNTER_NOTE = "open_encounter_note",
  ORDER_NOT_READY = "order_not_ready",
  ORDER_TYPE_OR_ROUTING = "order_type_or_routing",
  ORDER_UPDATE_REQUIRED = "order_update_required",
  OTHER = "other",
  PATIENT_INSURANCE_OON = "patient_insurance_oon",
  PATIENT_OUTREACH_BLOCKED = "patient_outreach_blocked",
  PAYER_REQUIRES_MANUAL_AUTH = "payer_requires_manual_auth",
  PEER_TO_PEER_REQUIRED = "peer_to_peer_required",
  PROVIDER_DOES_NOT_ACCEPT_FAXED_ORDERS = "provider_does_not_accept_faxed_orders",
  PROVIDER_INFORMATION_ISSUE = "provider_information_issue",
  PT_NEEDS_TO_UPDATE_INSURED_PCP = "pt_needs_to_update_insured_pcp",
  RECEIVING_DESTINATION_MISSING = "receiving_destination_missing",
  RECEIVING_PROVIDER_OON = "receiving_provider_oon",
  REQUIRED_DOCUMENTS_MISSING = "required_documents_missing",
  XRAY_ORDER = "xray_order",
}

export const orderEscalationReasonSchema = z.nativeEnum(OrderEscalationReason);

export const ORDER_ESCALATION_REASONS = Object.values(OrderEscalationReason);

export function isOrderEscalationReason(value: string): value is OrderEscalationReason {
  return ORDER_ESCALATION_REASONS.some((reason) => reason === value);
}

export enum OrderReworkReason {
  AUTH_RESOLVED_OR_OVERRIDDEN = "auth_resolved_or_overridden",
  AWAITING_INFORMATION = "awaiting_information",
  CANCELLED_OR_OUT_OF_SCOPE = "cancelled_or_out_of_scope",
  ENCOUNTER_NOTE_CLOSED = "encounter_note_closed",
  FACILITY_OR_PROVIDER_UPDATED = "facility_or_provider_updated",
  FACILITY_REQUESTED_INFO_OR_REFAX = "facility_requested_info_or_refax",
  FAX_TRANSMISSION_FAILED = "fax_transmission_failed",
  INSURANCE_UPDATED = "insurance_updated",
  NO_NEW_ACTION_NOTE = "no_new_action_note",
  ORDER_DETAILS_UPDATED = "order_details_updated",
  PCP_UPDATED = "pcp_updated",
  READY_TO_CONTINUE = "ready_to_continue",
}

export const orderReworkReasonSchema = z.nativeEnum(OrderReworkReason);

export const ORDER_REWORK_REASON_LABELS: Record<OrderReworkReason, string> = {
  [OrderReworkReason.AUTH_RESOLVED_OR_OVERRIDDEN]: "Authorization resolved or not required",
  [OrderReworkReason.AWAITING_INFORMATION]: "Awaiting information",
  [OrderReworkReason.CANCELLED_OR_OUT_OF_SCOPE]: "Cancelled or out of scope",
  [OrderReworkReason.ENCOUNTER_NOTE_CLOSED]: "Encounter note closed",
  [OrderReworkReason.FACILITY_OR_PROVIDER_UPDATED]: "Facility or provider updated",
  [OrderReworkReason.FACILITY_REQUESTED_INFO_OR_REFAX]: "Facility requested info or refax",
  [OrderReworkReason.FAX_TRANSMISSION_FAILED]: "Fax transmission failed",
  [OrderReworkReason.INSURANCE_UPDATED]: "Insurance / eligibility updated",
  [OrderReworkReason.NO_NEW_ACTION_NOTE]: "No new action note",
  [OrderReworkReason.ORDER_DETAILS_UPDATED]: "Order details updated (CPT / DX)",
  [OrderReworkReason.PCP_UPDATED]: "Insured PCP updated",
  [OrderReworkReason.READY_TO_CONTINUE]: "Ready to continue",
};

export enum OrderWorkflowStage {
  VERIFICATION = "verification",
  PROVIDER_SELECTION = "provider_selection",
  AUTHORIZATION = "authorization",
  SUBMISSION = "submission",
  COMPLETED = "completed",
  ESCALATED = "escalated",
}

export const TERMINAL_ORDER_WORKFLOW_STAGES = [OrderWorkflowStage.COMPLETED, OrderWorkflowStage.ESCALATED] as const;

export type TerminalOrderWorkflowStage = (typeof TERMINAL_ORDER_WORKFLOW_STAGES)[number];

export function isTerminalOrderWorkflowStage(stage: OrderWorkflowStage): stage is TerminalOrderWorkflowStage {
  return TERMINAL_ORDER_WORKFLOW_STAGES.some((terminal) => terminal === stage);
}

export type PatientOrderReopenLock = {
  workflowStage: TerminalOrderWorkflowStage;
  closedAt: string;
  closedAtField: "submissionCompletedAt" | "reassignNoteAfter" | "updatedAt";
  deletedAt: string | null;
};

export enum OrderProgressStatus {
  IN_PROCESS = "in_process",
  WAITING = "waiting",
  COMPLETED = "completed",
  ESCALATED = "escalated",
}

export enum OrderAuthorizationStatus {
  NOT_REQUIRED = "not_required",
  REQUIRED = "required",
  PENDING = "pending",
  APPROVED = "approved",
  NOT_APPROVED = "not_approved",
  CANCELLED_NOT_NEEDED = "cancelled_not_needed",
  /** Legacy value; treat as approved. */
  COMPLETED = "completed",
}

export function getOrderProgressStatus(order: {
  workflowStage: OrderWorkflowStage;
  authorizationStatus?: OrderAuthorizationStatus | null;
}): OrderProgressStatus {
  if (order.workflowStage === OrderWorkflowStage.COMPLETED) return OrderProgressStatus.COMPLETED;
  if (order.workflowStage === OrderWorkflowStage.ESCALATED) return OrderProgressStatus.ESCALATED;
  if (
    order.workflowStage === OrderWorkflowStage.AUTHORIZATION &&
    order.authorizationStatus === OrderAuthorizationStatus.PENDING
  ) {
    return OrderProgressStatus.WAITING;
  }

  return OrderProgressStatus.IN_PROCESS;
}

/** Athena order priority: `"1"` = STAT/high, `"2"` = routine/normal. */
export function isHighPriorityOrder(priority: string | null | undefined): boolean {
  return priority?.trim() === "1";
}

/** Outcome choices shown on the authorization details form. */
export const ORDER_AUTHORIZATION_STATUS_OPTIONS: {
  value:
    | OrderAuthorizationStatus.APPROVED
    | OrderAuthorizationStatus.NOT_APPROVED
    | OrderAuthorizationStatus.CANCELLED_NOT_NEEDED
    | OrderAuthorizationStatus.PENDING;
  label: string;
}[] = [
  { value: OrderAuthorizationStatus.APPROVED, label: "Approved" },
  { value: OrderAuthorizationStatus.NOT_APPROVED, label: "Not approved" },
  { value: OrderAuthorizationStatus.CANCELLED_NOT_NEEDED, label: "Cancelled/Not needed" },
  { value: OrderAuthorizationStatus.PENDING, label: "Pending" },
];

export type OrderAuthorizationOutcomeStatus = (typeof ORDER_AUTHORIZATION_STATUS_OPTIONS)[number]["value"];

export function toOrderAuthorizationOutcomeStatus(
  status: OrderAuthorizationStatus | null | undefined
): OrderAuthorizationOutcomeStatus | "" {
  if (status === OrderAuthorizationStatus.APPROVED || status === OrderAuthorizationStatus.COMPLETED) {
    return OrderAuthorizationStatus.APPROVED;
  }

  if (
    status === OrderAuthorizationStatus.NOT_APPROVED ||
    status === OrderAuthorizationStatus.CANCELLED_NOT_NEEDED ||
    status === OrderAuthorizationStatus.PENDING
  ) {
    return status;
  }

  return "";
}

export enum OrderAuthorizationSource {
  AVAILITY = "availity",
  UNITED_HEALTHCARE = "united_healthcare",
  EVICORE = "evicore",
  CARELON = "carelon",
  CIGNA = "cigna",
  FAX = "fax",
  OTHER = "other",
  PHONE = "phone",
}

export const selectedOrderProviderAddressSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }

    const address = value as Record<string, unknown>;

    return {
      line1: address.line1 ?? address.address1,
      line2: address.line2 ?? address.address2,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
    };
  },
  z.object({
    line1: z.string().min(1),
    line2: z.string().optional(),
    city: z.string().min(1),
    state: z.string().min(1),
    postalCode: z.string().optional(),
  })
);

export const selectedOrderProviderSchema = z
  .object({
    npi: z.string().nullable().optional(),
    source: z.enum(["ppn", "athena"]),
    clinicalProviderId: z.string().optional(),
    displayName: z.string().min(1),
    npiEnumerationType: npiEnumerationTypeSchema,
    firstName: z.string().min(1).optional(),
    lastName: z.string().min(1).optional(),
    address: selectedOrderProviderAddressSchema.optional(),
    phone: z.string().optional(),
    fax: z.string().optional(),
  })
  .superRefine((provider, ctx) => {
    if (provider.npiEnumerationType === "NPI-1" && (!provider.firstName || !provider.lastName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "NPI-1 providers require firstName and lastName",
        path: ["firstName"],
      });
    }
  });

export type SelectedOrderProviderAddress = z.infer<typeof selectedOrderProviderAddressSchema>;
export type SelectedOrderProvider = z.infer<typeof selectedOrderProviderSchema>;

export function toSelectedOrderProviderAddress(
  parts: AddressParts | null | undefined
): SelectedOrderProviderAddress | undefined {
  if (!parts?.line1 || !parts.city || !parts.state) {
    return undefined;
  }

  return {
    line1: parts.line1,
    ...(parts.line2 ? { line2: parts.line2 } : {}),
    city: parts.city,
    state: parts.state,
    ...(parts.postalCode ? { postalCode: parts.postalCode } : {}),
  };
}

export const patientOrderSchema = z.object({
  patientId: z.string().uuid(),
  ehrId: z.string(),
  orderType: z.nativeEnum(OrderType),
  status: z.string(),
  orderingProviderId: z.string().uuid().nullable(),
  dateOrdered: z.string().nullable(),
  description: z.string().nullable(),
  ehrOrderClassification: z.string().nullable(),
  orderClassification: z.string().nullable(),
  rawResponse: z.unknown().optional(),
  displayId: z.string().nullable().optional(),
  clinicalProviderEhrId: z.string().nullable(),
  clinicalProviderName: z.string().nullable(),
  clinicalProviderNpi: z.string().nullable(),
  procedureCode: z.string().nullable(),
  departmentId: z.string().nullable(),
  priority: z.string().nullable(),
  diagnoses: z.array(z.unknown()),
  verificationDecision: z.nativeEnum(OrderVerificationDecision).nullable(),
  verificationReason: z.string().nullable(),
  escalationReason: orderEscalationReasonSchema.nullable(),
  escalationDetails: z.string().nullable(),
  escalatedFromTaskType: z
    .enum([
      "order_qualify",
      "order_reassign_qualify",
      "order_provider_selection",
      "order_auth_check",
      "order_auth_submit",
      "order_ehr_sync",
    ])
    .nullable(),
  workflowStage: z.nativeEnum(OrderWorkflowStage),
  selectedProvider: selectedOrderProviderSchema.nullable(),
  authorizationStatus: z.nativeEnum(OrderAuthorizationStatus).nullable(),
  authorizationSource: z.string().nullable(),
  authorizationId: z.string().nullable(),
  authorizationNotes: z.string().nullable(),
  authorizationCompletedAt: z.string().nullable(),
  submissionCompletedAt: z.string().nullable(),
  submissionPatientCaseAt: z.string().nullable(),
  submissionSmsSentAt: z.string().nullable(),
  workflowAttempt: z.number().int().positive(),
  reassignNoteAfter: z.string().nullable(),
});

export type PatientOrder = z.infer<typeof patientOrderSchema> & DatabaseDefaults;

export type OrderClinicalAssignment = {
  clinicalProviderId: string;
  name: string | null;
  npi: string | null;
  address: AddressParts | null;
  phone: string | null;
  fax: string | null;
};

export type LivePatientContact = {
  preferredName: string | null;
  consentToText: boolean | null;
  portalAccessGiven: boolean;
};

export type PatientOrderEscalation = {
  reason: OrderEscalationReason;
  workflowAttempt: number;
  createdAt: string;
};

export type PatientOrderRework = {
  reason: OrderReworkReason;
  workflowAttempt: number;
  createdAt: string;
};

export type PatientOrderWithPatient = PatientOrder & {
  patient?: { givenName: string; familyName: string; displayId: string };
  patientDetails?: Patient | null;
  orderingProviderPractitioner?: Practitioner | null;
  orderedClinicalAssignment?: OrderClinicalAssignment | null;
  liveContact?: LivePatientContact | null;
  escalations?: PatientOrderEscalation[];
  reworks?: PatientOrderRework[];
  priorAuthRequirement?: OrderPriorAuthRequirement | null;
};

export const patientOrderShape: PatientOrder = {
  id: "",
  groupId: "",
  patientId: "",
  ehrId: "",
  orderType: OrderType.OTHER,
  status: "",
  orderingProviderId: null,
  dateOrdered: null,
  description: null,
  ehrOrderClassification: null,
  orderClassification: null,
  rawResponse: {},
  displayId: null,
  clinicalProviderEhrId: null,
  clinicalProviderName: null,
  clinicalProviderNpi: null,
  procedureCode: null,
  departmentId: null,
  priority: null,
  diagnoses: [],
  verificationDecision: null,
  verificationReason: null,
  escalationReason: null,
  escalationDetails: null,
  escalatedFromTaskType: null,
  workflowStage: OrderWorkflowStage.VERIFICATION,
  selectedProvider: null,
  authorizationStatus: null,
  authorizationSource: null,
  authorizationId: null,
  authorizationNotes: null,
  authorizationCompletedAt: null,
  submissionCompletedAt: null,
  submissionPatientCaseAt: null,
  submissionSmsSentAt: null,
  workflowAttempt: 1,
  reassignNoteAfter: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
};

export type PatientOrderSyncPayload = Omit<
  PatientOrder,
  | "id"
  | "createdAt"
  | "updatedAt"
  | "verificationDecision"
  | "verificationReason"
  | "escalationReason"
  | "escalationDetails"
  | "escalatedFromTaskType"
  | "workflowStage"
  | "selectedProvider"
  | "authorizationStatus"
  | "authorizationSource"
  | "authorizationId"
  | "authorizationNotes"
  | "authorizationCompletedAt"
  | "submissionCompletedAt"
  | "submissionPatientCaseAt"
  | "submissionSmsSentAt"
  | "workflowAttempt"
  | "reassignNoteAfter"
>;
